// Shared consent dialog state for the widget sign bridge. Both the widget
// modal and the inline embed use this: it owns the promise-driven consent
// dialog (open + a parked resolver settled by Allow / Not now). The grant
// itself is persisted by the bridge hook on approval (see
// use-widget-sign-bridge.ts).

import { useCallback, useEffect, useRef, useState } from 'react';

export interface WidgetSignConsentControls {
  /** Open the consent dialog; resolves true on Allow, false otherwise. */
  requestConsent: () => Promise<boolean>;
  /** Whether the consent dialog is open. */
  dialogOpen: boolean;
  /** Settle the open dialog with approval. */
  allow: () => void;
  /** Settle the open dialog with refusal (also covers dismiss). */
  deny: () => void;
}

export function useWidgetSignConsent(): WidgetSignConsentControls {
  const [dialogOpen, setDialogOpen] = useState(false);
  const resolverRef = useRef<((ok: boolean) => void) | null>(null);
  const pendingRef = useRef<Promise<boolean> | null>(null);

  const settle = useCallback((ok: boolean) => {
    setDialogOpen(false);
    const resolve = resolverRef.current;
    resolverRef.current = null;
    pendingRef.current = null;
    resolve?.(ok);
  }, []);

  // Concurrent sign requests (a widget posting two before the user answers)
  // must share ONE consent outcome. Returning the in-flight promise instead
  // of overwriting the resolver prevents orphaning the first request — which
  // would otherwise never get a bridge reply.
  const requestConsent = useCallback(() => {
    if (pendingRef.current) return pendingRef.current;
    const p = new Promise<boolean>((resolve) => { resolverRef.current = resolve; });
    pendingRef.current = p;
    setDialogOpen(true);
    return p;
  }, []);

  // If the host unmounts (modal closed, navigated away) while a consent
  // request is parked, settle it as a denial so the bridge awaiting it
  // doesn't hang. No setState here — the component is already gone.
  useEffect(() => () => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    pendingRef.current = null;
    resolve?.(false);
  }, []);

  return {
    requestConsent,
    dialogOpen,
    allow: useCallback(() => settle(true), [settle]),
    deny: useCallback(() => settle(false), [settle]),
  };
}
